import React, { useEffect, useState } from 'react';
import { Table, Modal, Form, Button, Badge, InputGroup, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../api/client';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/format';

const STATUSES = ['new', 'contacted', 'site_visit', 'negotiation', 'booked', 'lost'];
const SOURCES = ['walk_in', 'referral', 'website', 'social', 'broker', 'other'];

const empty = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  source: 'other',
  project: '',
  interestedUnitType: '',
  budget: 0,
  status: 'new',
  notes: '',
  nextFollowUp: '',
  salesAgent: '',
};

const statusColor = {
  new: 'secondary', contacted: 'info', site_visit: 'primary',
  negotiation: 'warning', booked: 'success', lost: 'danger',
};

const Leads = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const [{ data: leads }, { data: projs }] = await Promise.all([
        client.get('/leads', { params }),
        client.get('/projects'),
      ]);
      setItems(leads);
      setProjects(projs);
      if (user.role !== 'sales') {
        try {
          const { data: users } = await client.get('/users', { params: { role: 'sales' } });
          setAgents(users);
        } catch {}
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...empty,
      ...it,
      project: it.project?._id || it.project || '',
      salesAgent: it.salesAgent?._id || it.salesAgent || '',
      nextFollowUp: it.nextFollowUp ? it.nextFollowUp.slice(0, 10) : '',
    });
    setShow(true);
  };
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.project) delete payload.project;
    if (!payload.salesAgent) delete payload.salesAgent;
    if (!payload.nextFollowUp) delete payload.nextFollowUp;
    try {
      if (editing) {
        await client.put(`/leads/${editing._id}`, payload);
        toast.success('Lead updated');
      } else {
        await client.post('/leads', payload);
        toast.success('Lead created');
      }
      setShow(false);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.message || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await client.delete(`/leads/${id}`);
    toast.info('Lead deleted');
    load();
  };

  return (
    <div>
      <PageHeader title="Leads" subtitle="Customer enquiries from sales team" actionLabel="New Lead" onAction={openNew} />

      <div className="d-flex gap-2 mb-3">
        <InputGroup style={{ maxWidth: 320 }}>
          <Form.Control placeholder="Search name / phone / email" value={q} onChange={(e) => setQ(e.target.value)} />
        </InputGroup>
        <Form.Select style={{ maxWidth: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </Form.Select>
        <Button onClick={load}><i className="bi bi-search"></i> Search</Button>
      </div>

      {loading ? <Spinner animation="border" /> : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Customer</th><th>Project</th><th>Sales Agent</th><th>Status</th>
              <th>Source</th><th>Next Follow-up</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-4">No leads</td></tr>
            )}
            {items.map((l) => (
              <tr key={l._id}>
                <td><strong>{l.customerName}</strong><br /><small className="text-muted">{l.customerPhone}</small></td>
                <td>{l.project?.name || '-'}</td>
                <td>{l.salesAgent?.name || '-'}</td>
                <td><Badge bg={statusColor[l.status] || 'secondary'} className="text-uppercase">{l.status}</Badge></td>
                <td>{l.source}</td>
                <td>{fmtDate(l.nextFollowUp)}</td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" onClick={() => openEdit(l)}>
                    <i className="bi bi-pencil"></i>
                  </Button>{' '}
                  {user.role === 'admin' && (
                    <Button size="sm" variant="outline-danger" onClick={() => remove(l._id)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Form onSubmit={save}>
          <Modal.Header closeButton><Modal.Title>{editing ? 'Edit Lead' : 'New Lead'}</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6"><Form.Label>Customer Name *</Form.Label>
                <Form.Control required value={form.customerName} onChange={(e) => onChange('customerName', e.target.value)} /></div>
              <div className="col-md-3"><Form.Label>Phone *</Form.Label>
                <Form.Control required value={form.customerPhone} onChange={(e) => onChange('customerPhone', e.target.value)} /></div>
              <div className="col-md-3"><Form.Label>Email</Form.Label>
                <Form.Control type="email" value={form.customerEmail} onChange={(e) => onChange('customerEmail', e.target.value)} /></div>
              <div className="col-md-4"><Form.Label>Project</Form.Label>
                <Form.Select value={form.project} onChange={(e) => onChange('project', e.target.value)}>
                  <option value="">-</option>
                  {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </Form.Select></div>
              <div className="col-md-4"><Form.Label>Source</Form.Label>
                <Form.Select value={form.source} onChange={(e) => onChange('source', e.target.value)}>
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Form.Select></div>
              <div className="col-md-4"><Form.Label>Status</Form.Label>
                <Form.Select value={form.status} onChange={(e) => onChange('status', e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Form.Select></div>
              <div className="col-md-4"><Form.Label>Interested Unit Type</Form.Label>
                <Form.Control placeholder="2BHK / 3BHK" value={form.interestedUnitType} onChange={(e) => onChange('interestedUnitType', e.target.value)} /></div>
              <div className="col-md-4"><Form.Label>Budget (₹)</Form.Label>
                <Form.Control type="number" value={form.budget} onChange={(e) => onChange('budget', Number(e.target.value))} /></div>
              <div className="col-md-4"><Form.Label>Next Follow-up</Form.Label>
                <Form.Control type="date" value={form.nextFollowUp} onChange={(e) => onChange('nextFollowUp', e.target.value)} /></div>
              {user.role !== 'sales' && (
                <div className="col-md-6"><Form.Label>Sales Agent</Form.Label>
                  <Form.Select value={form.salesAgent} onChange={(e) => onChange('salesAgent', e.target.value)}>
                    <option value="">-</option>
                    {agents.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
                  </Form.Select></div>
              )}
              <div className="col-12"><Form.Label>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.notes} onChange={(e) => onChange('notes', e.target.value)} /></div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button type="submit" variant="primary">{editing ? 'Save' : 'Create'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Leads;
